import { feedRecordKey } from '@parcel/types';
import { Button, cn } from '@parcel/ui';
import { Building2, ChevronDown, ChevronUp, FileText, Tag, createLucideIcon } from 'lucide-react';
import type * as React from 'react';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import type { FeedRow as FeedRowData } from '../../lib/feed.js';
import { logoUrl } from '../../lib/logo.js';
import type { Score } from '../../lib/scores.js';
import { PRIORITY_DOT_CLASS } from '../../lib/signal-labels.js';
import type { ApiSignalType } from '../../lib/signals.js';
import { EntityAvatar } from '../entity-drawer/entity-drawer-shell.js';
import { getSignalMeta } from '../entity-drawer/signal-feed.js';
import { Hint } from '../hint.js';
import { ScoreBar } from '../scoring/score-cells.js';
import { shortDate } from './feed-format.js';

// Lucide's sticky-note-check (ISC), newer than the lucide-react this app pins.
const StickyNoteCheck = createLucideIcon('StickyNoteCheck', [
  ['path', { d: 'm15 19 2 2 4-4', key: 'check' }],
  ['path', { d: 'M15 3v5a1 1 0 0 0 1 1h5', key: 'fold' }],
  [
    'path',
    {
      d: 'M21 13V9a2.4 2.4 0 0 0-.706-1.706l-3.588-3.588A2.4 2.4 0 0 0 15 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6.5',
      key: 'note',
    },
  ],
]);

/** One column template for every row, so the columns line up down the page. */
const ROW_GRID = 'grid grid-cols-[16px_32px_minmax(0,1fr)_128px_80px_28px_16px] items-center gap-4';

interface FeedRowProps {
  row: FeedRowData;
  /** The row's own score: the ICP Score on an account, the Atlas Score on a project. */
  score?: Score;
  index: number;
  expanded: boolean;
  focused: boolean;
  onToggle: () => void;
  onMarkRead: () => void;
  children?: React.ReactNode;
}

/** A folded Feed row: the record, its newest news, two labelled columns, and Mark read while unread. */
export function FeedRow({
  row,
  score,
  index,
  expanded,
  focused,
  onToggle,
  onMarkRead,
  children,
}: FeedRowProps) {
  return (
    <li
      data-feed-key={feedRecordKey(row)}
      data-focused={focused || undefined}
      data-unseen={row.unseen || undefined}
      className={cn(
        'overflow-hidden rounded-xl border bg-card transition-shadow hover:shadow-sm',
        focused && 'ring-1 ring-brand/40',
      )}
    >
      <div className={cn('relative px-4 py-3', ROW_GRID)}>
        <span className="relative text-center text-xs text-muted-foreground">{index + 1}</span>
        <span className="relative z-10">
          <FeedLogo row={row} />
        </span>
        <FeedRowTitle row={row} expanded={expanded} onToggle={onToggle} />
        <Column label="Score">
          <RowScore row={row} score={score} />
        </Column>
        <Column label="Last update">{shortDate(row.last_activity_at)}</Column>
        <span className="relative z-10 flex justify-center">
          {row.unseen ? <MarkReadButton onMarkRead={onMarkRead} /> : null}
        </span>
        <span className="flex justify-center text-muted-foreground" aria-hidden>
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </span>
      </div>
      {expanded ? children : null}
    </li>
  );
}

/** The drawers' avatar: an account's logo links to its site, a project's to the portal that filed it. */
function FeedLogo({ row }: { row: FeedRowData }) {
  if (row.dataset === 'account') {
    return (
      <EntityAvatar
        src={logoUrl(row.website)}
        alt={row.name}
        fallbackIcon={Building2}
        size="sm"
        href={normalizeExternalUrl(row.website)}
        hrefLabel={`Open ${row.name}'s website`}
      />
    );
  }
  return (
    <EntityAvatar
      src={logoUrl(row.latest_source?.logo_domain)}
      alt={row.latest_source?.display_name ?? row.name}
      fallbackIcon={Tag}
      size="sm"
      href={normalizeExternalUrl(row.latest_source?.base_url)}
      hrefLabel={row.latest_source ? `Open ${row.latest_source.display_name}` : undefined}
    />
  );
}

/** The record's name and newest news; the button stretches over the card, so a click anywhere opens it. */
function FeedRowTitle({
  row,
  expanded,
  onToggle,
}: {
  row: FeedRowData;
  expanded: boolean;
  onToggle: () => void;
}) {
  const headline = row.headline;
  const meta = headline ? getSignalMeta(headline.signal_type as ApiSignalType) : null;
  const newest = row.items[0];
  const oneLiner =
    headline?.summary ??
    (newest?.kind === 'document' ? (newest.label ?? newest.document_type ?? '') : '');
  return (
    <span className="min-w-0">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="block max-w-full truncate text-left text-sm font-medium after:absolute after:inset-0 after:content-['']"
      >
        {row.name}
      </button>
      <span className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        <SignalChip
          label={meta?.label ?? (row.item_count === 1 ? 'New document' : 'New documents')}
          dot={headline ? PRIORITY_DOT_CLASS[headline.priority] : null}
        />
        <span className="max-w-[min(26rem,60%)] truncate">{oneLiner}</span>
      </span>
    </span>
  );
}

/** A signal's type with its priority's dot, as the row and the expanded signals show it. */
export function SignalChip({ label, dot }: { label: string; dot: string | null }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border bg-background px-1.5 py-px text-[11px] text-foreground">
      {dot ? (
        <span className={cn('size-1.5 rounded-full', dot)} />
      ) : (
        <FileText className="size-3 text-muted-foreground" />
      )}
      {label}
    </span>
  );
}

function RowScore({ row, score }: { row: FeedRowData; score: Score | undefined }) {
  // An API older than the web bundle sends no scores during a deploy.
  const value = row.scores?.[row.dataset === 'account' ? 'icp' : 'atlas'];
  if (!value) return <span className="text-muted-foreground">-</span>;
  // Left, so the card never covers the row's buttons on the right.
  return <ScoreBar entity={row.dataset} value={value} score={score} side="left" />;
}

function MarkReadButton({ onMarkRead }: { onMarkRead: () => void }) {
  return (
    <Hint label="Mark read">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 text-muted-foreground hover:text-foreground"
        aria-label="Mark read"
        onClick={onMarkRead}
      >
        <StickyNoteCheck className="size-4" aria-hidden />
      </Button>
    </Hint>
  );
}

function Column({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="min-w-0">
      <span className="mb-1 block text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="block truncate text-sm">{children}</span>
    </span>
  );
}
